import { useMemo, useRef } from "react"
import { z } from "zod"
import { MemoriesApi, Memory } from "@common/types/memory"
import { useAuthHeader } from "../context/AuthContext"
import { useDioptraFetch } from "./use-dioptra-fetch"
import { useAnalytics } from "../context/AnalyticsContext"
import { getEnv } from "../utils/envManager"
import {
  ConversationMemory,
  getConversationMemories,
  deleteConversationMemory,
  updateConversationMemory,
  addMemoryTag,
  removeMemoryTag,
} from "../utils/web-api"

const tagsResponseSchema = z.object({
  tags: z.array(z.object({ id: z.string(), name: z.string() })),
})

const toMemory = (memory: ConversationMemory): Memory => ({
  id: memory.id,
  conversationId: memory.conversationId,
  contentText: memory.contentText,
  contextText: memory.contextText,
  createdAt: memory.createdAt,
  tags: (memory.tags ?? []).map(t => ({ id: t.id, tag: { id: t.tag.id, name: t.tag.name } })),
})

export function useMemoriesApi(): MemoriesApi {
  const authHeader = useAuthHeader()
  const authHeaderRef = useRef(authHeader)
  authHeaderRef.current = authHeader

  const { dioptraFetch } = useDioptraFetch()
  const { sendError } = useAnalytics()

  return useMemo<MemoriesApi>(() => {
    const requireAuth = () => {
      const header = authHeaderRef.current
      if (!header) throw new Error("Not authenticated")
      return header
    }

    return {
      supportsTags: true,
      list: async () => (await getConversationMemories(requireAuth())).map(toMemory),
      remove: id => deleteConversationMemory(requireAuth(), id),
      update: async (id, fields) =>
        toMemory(await updateConversationMemory(requireAuth(), id, fields)),
      addTag: (id, tagName) => addMemoryTag(requireAuth(), id, tagName),
      removeTag: (id, joinId) => removeMemoryTag(requireAuth(), id, joinId),
      searchTags: async query => {
        try {
          const raw = await dioptraFetch(
            `${getEnv("WEBAPP_API_BASE_URL")}/api/tags?search=${encodeURIComponent(query)}&limit=10`,
          )
          const parsed = tagsResponseSchema.safeParse(raw)
          if (!parsed.success) {
            sendError(new Error("Invalid /api/tags response shape"), {
              component: "useMemoriesApi",
              action: "validate_tag_response",
              query,
            })
            return []
          }
          return parsed.data.tags
        } catch (err) {
          sendError(err as Error, { component: "useMemoriesApi", action: "search_tags", query })
          throw err
        }
      },
    }
  }, [dioptraFetch, sendError])
}
